import { supabase } from "../supabase";

const EPSILON = 0.000001;
const CANCELLED_PURCHASE_STATUSES = new Set([
  "anulado",
  "anulada",
  "annulled",
  "cancelled",
  "cancelado",
]);

const normalizeText = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();

export const normalizeMoney = (value) => {
  const amount = Number(value);
  return Number.isFinite(amount) ? amount : 0;
};

export const clampMoney = (value, max) => {
  const safeValue = Math.max(0, normalizeMoney(value));
  const safeMax = Math.max(0, normalizeMoney(max));
  return Math.min(safeValue, safeMax);
};

export const buildSuggestedAllocations = (
  documents = [],
  totalAmount = 0,
  openAmountField = "open_amount",
) => {
  let remaining = Math.max(0, normalizeMoney(totalAmount));

  return (Array.isArray(documents) ? documents : []).map((document) => {
    const openAmount = Math.max(0, normalizeMoney(document?.[openAmountField]));
    const allocation = Math.min(openAmount, remaining);
    remaining -= allocation;

    return {
      ...document,
      allocation,
    };
  });
};

export const sumAllocationAmounts = (documents = []) =>
  (Array.isArray(documents) ? documents : []).reduce(
    (acc, document) => acc + Math.max(0, normalizeMoney(document?.allocation)),
    0,
  );

export const parseProviderPaymentApplications = (items = []) =>
  (Array.isArray(items) ? items : [])
    .filter(
      (item) =>
        item &&
        item.kind === "purchase_application" &&
        item.purchase_id &&
        normalizeMoney(item.amount) > EPSILON,
    )
    .map((item) => ({
      purchase_id: item.purchase_id,
      amount: Math.max(0, normalizeMoney(item.amount)),
      purchase_total: Math.max(0, normalizeMoney(item.purchase_total)),
      purchase_number: item.purchase_number || "",
      purchase_type: item.purchase_type || "",
      purchase_date: item.purchase_date || null,
    }));

const isCancelledPurchase = (purchase) =>
  CANCELLED_PURCHASE_STATUSES.has(normalizeText(purchase?.estado));

const isPaymentPurchase = (purchase) =>
  normalizeText(purchase?.tipo_comprobante) === "pago";

const buildAppliedAmountByPurchase = (paymentRows = []) => {
  const appliedByPurchase = new Map();

  (paymentRows || []).forEach((payment) => {
    if (isCancelledPurchase(payment)) return;
    if (!isPaymentPurchase(payment)) return;

    parseProviderPaymentApplications(payment?.items).forEach((application) => {
      const current = appliedByPurchase.get(application.purchase_id) || 0;
      appliedByPurchase.set(
        application.purchase_id,
        current + Math.max(0, normalizeMoney(application.amount)),
      );
    });
  });

  return appliedByPurchase;
};

export async function fetchOpenProviderPurchases({
  organizationId,
  providerId,
  supabaseClient = supabase,
}) {
  if (!organizationId || !providerId) {
    return { data: [], error: null };
  }

  const [purchasesResponse, paymentsResponse] = await Promise.all([
    supabaseClient
      .from("compras")
      .select(
        "id, fecha, created_at, tipo_comprobante, numero_comprobante, total_general, estado, condicion_pago, observaciones",
      )
      .eq("organization_id", organizationId)
      .eq("proveedor_id", providerId)
      .order("fecha", { ascending: true }),
    supabaseClient
      .from("compras")
      .select("id, estado, items")
      .eq("organization_id", organizationId)
      .eq("proveedor_id", providerId)
      .eq("tipo_comprobante", "Pago"),
  ]);

  if (purchasesResponse.error) {
    return { data: [], error: purchasesResponse.error };
  }

  if (paymentsResponse.error) {
    return { data: [], error: paymentsResponse.error };
  }

  const appliedByPurchase = buildAppliedAmountByPurchase(paymentsResponse.data || []);

  const data = (purchasesResponse.data || [])
    .filter((purchase) => !isCancelledPurchase(purchase))
    .filter((purchase) => !isPaymentPurchase(purchase))
    .map((purchase) => {
      const totalAmount = Math.max(0, normalizeMoney(purchase.total_general));
      const appliedAmount = Math.max(
        0,
        normalizeMoney(appliedByPurchase.get(purchase.id) || 0),
      );
      const openAmount = Math.max(0, totalAmount - appliedAmount);

      return {
        ...purchase,
        total_amount: totalAmount,
        applied_amount: appliedAmount,
        open_amount: openAmount,
      };
    })
    .filter((purchase) => purchase.open_amount > EPSILON)
    .sort((left, right) => {
      const leftTime = new Date(left.fecha || left.created_at || 0).getTime();
      const rightTime = new Date(right.fecha || right.created_at || 0).getTime();
      return leftTime - rightTime;
    });

  return { data, error: null };
}

export function buildProviderPaymentItems(allocations = [], purchases = []) {
  const purchasesById = new Map(
    (Array.isArray(purchases) ? purchases : [])
      .filter((purchase) => purchase?.id)
      .map((purchase) => [purchase.id, purchase]),
  );

  return (Array.isArray(allocations) ? allocations : [])
    .filter(
      (allocation) =>
        allocation?.purchase_id &&
        Math.max(0, normalizeMoney(allocation.amount)) > EPSILON,
    )
    .map((allocation) => {
      const purchase = purchasesById.get(allocation.purchase_id);
      return {
        kind: "purchase_application",
        purchase_id: allocation.purchase_id,
        amount: Math.max(0, normalizeMoney(allocation.amount)),
        purchase_total: Math.max(0, normalizeMoney(purchase?.total_general)),
        purchase_number: purchase?.numero_comprobante || "",
        purchase_type: purchase?.tipo_comprobante || "Compra",
        purchase_date: purchase?.fecha || purchase?.created_at || null,
      };
    });
}

export async function recomputeProviderPurchaseStatuses({
  organizationId,
  providerId,
  purchaseIds = [],
  supabaseClient = supabase,
}) {
  const uniquePurchaseIds = Array.from(
    new Set((Array.isArray(purchaseIds) ? purchaseIds : []).filter(Boolean)),
  );

  if (!organizationId || !providerId || uniquePurchaseIds.length === 0) {
    return { applied: false, updated: 0 };
  }

  const [purchasesResponse, paymentsResponse] = await Promise.all([
    supabaseClient
      .from("compras")
      .select("id, total_general, estado, tipo_comprobante, monto_pagado")
      .eq("organization_id", organizationId)
      .eq("proveedor_id", providerId)
      .in("id", uniquePurchaseIds),
    supabaseClient
      .from("compras")
      .select("id, estado, items")
      .eq("organization_id", organizationId)
      .eq("proveedor_id", providerId)
      .eq("tipo_comprobante", "Pago"),
  ]);

  if (purchasesResponse.error) throw purchasesResponse.error;
  if (paymentsResponse.error) throw paymentsResponse.error;

  const appliedByPurchase = buildAppliedAmountByPurchase(paymentsResponse.data || []);
  let updated = 0;

  for (const purchase of purchasesResponse.data || []) {
    if (!purchase?.id || isPaymentPurchase(purchase) || isCancelledPurchase(purchase)) {
      continue;
    }

    const totalAmount = Math.max(0, normalizeMoney(purchase.total_general));
    const appliedAmount = Math.max(
      0,
      normalizeMoney(appliedByPurchase.get(purchase.id) || 0),
    );

    let nextStatus = "Pendiente";
    if (appliedAmount >= totalAmount - EPSILON) nextStatus = "Pagada";
    else if (appliedAmount > EPSILON) nextStatus = "Parcial";

    // Persistimos también monto_pagado: flujoFondos lo usa para proyectar solo el
    // remanente impago y no contar dos veces lo ya pagado de una compra Parcial. Bug T5.
    const statusUnchanged = String(purchase.estado || "") === nextStatus;
    const pagadoUnchanged = Math.abs(normalizeMoney(purchase.monto_pagado) - appliedAmount) < EPSILON;
    if (statusUnchanged && pagadoUnchanged) continue;

    const { error: updateError } = await supabaseClient
      .from("compras")
      .update({ estado: nextStatus, monto_pagado: appliedAmount })
      .eq("id", purchase.id);

    if (updateError) throw updateError;
    updated += 1;
  }

  return { applied: true, updated };
}
